<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import SettingsLayout from 'dashboard/components-next/Settings/SettingsLayout.vue';
import DriveCard from './components/DriveCard.vue';
import AddDriveModal from './components/AddDriveModal.vue';
import DrivePickerModal from './components/DrivePickerModal.vue';

const store = useStore();
const getters = useStoreGetters();
const { t } = useI18n();

const showAddModal = ref(false);
const showPickerModal = ref(false);
const pickerDriveId = ref(null);
const accountId = computed(() => getters.getCurrentAccountId?.value);
const uiFlags = computed(() => getters['drives/getUIFlags'].value || {});
const drives = computed(() => getters['drives/getDrives'].value || []);

const isLoading = computed(() => uiFlags.value.isFetching);
const isEmpty = computed(() => drives.value.length === 0);

async function openPickerForDrive(drive) {
  if (!drive?.id) return;
  if (drive.provider === 'google') {
    try {
      const { useGooglePicker } = await import(
        'dashboard/composables/useGooglePicker'
      );
      const picker = useGooglePicker();
      await picker.buildAndShowPickerInPlace({
        driveId: drive.id,
        onSelect: () => {},
        onCancel: () => {},
      });
    } catch (err) {
      useAlert(
        err?.message || t('INTEGRATION_SETTINGS.DRIVES.VIEW_FILES_ERROR')
      );
    }
    return;
  }
  pickerDriveId.value = drive.id;
  showPickerModal.value = true;
}

onMounted(() => {
  store.dispatch('drives/get');
});

const openAddModal = () => {
  showAddModal.value = true;
};

const closeAddModal = () => {
  showAddModal.value = false;
};

const handleTestConnection = async driveId => {
  try {
    await store.dispatch('drives/testConnection', driveId);
    useAlert(t('INTEGRATION_SETTINGS.DRIVES.TEST_CONNECTION.SUCCESS'));
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.DRIVES.TEST_CONNECTION.ERROR')
    );
  }
};

const handleReauthorize = async drive => {
  try {
    const response = await store.dispatch('drives/reauthorize', drive.id);
    if (response.authorization_url) {
      window.location.href = response.authorization_url;
    }
  } catch (error) {
    useAlert(
      error?.message || t('INTEGRATION_SETTINGS.DRIVES.REAUTHORIZE.ERROR')
    );
  }
};

const handleDelete = async driveId => {
  // eslint-disable-next-line no-alert
  if (window.confirm(t('INTEGRATION_SETTINGS.DRIVES.DELETE.CONFIRM'))) {
    try {
      await store.dispatch('drives/delete', driveId);
      useAlert(t('INTEGRATION_SETTINGS.DRIVES.DELETE.SUCCESS'));
    } catch (error) {
      useAlert(error?.message || t('INTEGRATION_SETTINGS.DRIVES.DELETE.ERROR'));
    }
  }
};
</script>

<template>
  <SettingsLayout
    :header-title="$t('INTEGRATION_SETTINGS.DRIVES.HEADER')"
    :button-label="$t('INTEGRATION_SETTINGS.DRIVES.ADD_BUTTON')"
    @click="openAddModal"
  >
    <template #action>
      <AddDriveModal v-if="showAddModal" @close="closeAddModal" />
      <DrivePickerModal
        v-model:show="showPickerModal"
        :drive-id="pickerDriveId"
        :viewer-mode="true"
        @cancel="showPickerModal = false"
      />
    </template>

    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <Spinner />
    </div>

    <!-- Empty State -->
    <div
      v-else-if="isEmpty"
      class="flex flex-col items-center justify-center py-12 text-center"
    >
      <div
        class="w-16 h-16 mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center"
      >
        <Icon icon="i-lucide-hard-drive" class="w-8 h-8 text-slate-400" />
      </div>
      <h3 class="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">
        {{ $t('INTEGRATION_SETTINGS.DRIVES.EMPTY_STATE') }}
      </h3>
      <p class="text-slate-600 dark:text-slate-400 mb-6 max-w-md">
        {{ $t('INTEGRATION_SETTINGS.DRIVES.DESCRIPTION') }}
      </p>
      <Button
        :label="$t('INTEGRATION_SETTINGS.DRIVES.ADD_FIRST_DRIVE')"
        icon="i-lucide-plus"
        @click="openAddModal"
      />
    </div>

    <!-- Drive List -->
    <div v-else class="space-y-4">
      <DriveCard
        v-for="drive in drives"
        :key="drive.id"
        :drive="drive"
        :pick-file-providers="['google', 'onedrive', 'sharepoint']"
        :viewer-mode="true"
        @test-connection="handleTestConnection"
        @reauthorize="handleReauthorize"
        @delete="handleDelete"
        @pick-file="openPickerForDrive"
      />
    </div>
  </SettingsLayout>
</template>
